import { useState, useEffect } from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  useRecordContext,
  TopToolbar,
  EditButton,
  Loading,
} from 'react-admin';
import {
  Card, CardContent, Grid, Typography, Box, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { UserActivityChart } from '../../components/users/UserActivityChart';
import { TokenDistributionChart } from '../../components/users/TokenDistributionChart';
import { BanButton } from '../../components/users/BanButton';

const UserShowActions = () => (
  <TopToolbar>
    <BanButton />
    <EditButton />
  </TopToolbar>
);

export const UserShow = () => {
  return (
    <Show actions={<UserShowActions />}>
      <TabbedShowLayout>
        <Tab label="Profile">
          <UserProfileSection />
        </Tab>

        <Tab label="Statistics">
          <UserStatsSection />
        </Tab>

        <Tab label="Activity">
          <UserActivitySection />
        </Tab>

        <Tab label="Bets History">
          <UserBetsSection />
        </Tab>

        <Tab label="Prizes">
          <UserPrizesSection />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const UserProfileSection = () => {
  const record = useRecordContext();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Telegram Profile
            </Typography>
            <Box sx={{ mt: 2 }}>
              <InfoRow label="User ID" value={record?.id} />
              <InfoRow
                label="Username"
                value={record?.username ? `@${record.username}` : 'N/A'}
              />
              <InfoRow label="First Name" value={record?.first_name} />
              <InfoRow label="Last Name" value={record?.last_name || 'N/A'} />
              <InfoRow label="Language" value={record?.language_code || 'N/A'} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                {record?.is_blacklisted ? (
                  <Chip label="BANNED" color="error" />
                ) : (
                  <Chip label="ACTIVE" color="success" />
                )}
              </Box>
              <InfoRow
                label="Registered"
                value={
                  record?.registered_at
                    ? new Date(record.registered_at).toLocaleDateString()
                    : 'N/A'
                }
              />
              <InfoRow
                label="Last Bet"
                value={
                  record?.last_bet_at
                    ? new Date(record.last_bet_at).toLocaleDateString()
                    : 'Never'
                }
              />
              {record?.banned_at && (
                <InfoRow
                  label="Banned At"
                  value={new Date(record.banned_at).toLocaleDateString()}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const UserStatsSection = () => {
  const record = useRecordContext();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <StatCard label="Total Bets" value={record?.total_bets || 0} />
      </Grid>
      <Grid item xs={12} md={3}>
        <StatCard
          label="Total Volume"
          value={`$${formatNumber(record?.total_volume_usd || 0)}`}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <StatCard
          label="Avg Bet"
          value={`$${formatNumber(record?.avg_bet_usd || 0)}`}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <StatCard
          label="Win Rate"
          value={`${(record?.win_rate_percent || 0).toFixed(1)}%`}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <StatCard label="Wins" value={record?.wins || 0} color="#4caf50" />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatCard label="Losses" value={record?.losses || 0} color="#f44336" />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatCard label="Favorite Token" value={record?.favorite_token || 'N/A'} />
      </Grid>

      <Grid item xs={12} md={6}>
        <StatCard label="Total Prizes" value={record?.total_prizes || 0} />
      </Grid>
      <Grid item xs={12} md={6}>
        <StatCard
          label="Prizes Won"
          value={`$${formatNumber(record?.total_prizes_usd || 0)}`}
        />
      </Grid>
    </Grid>
  );
};

const UserActivitySection = () => {
  const record = useRecordContext();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Activity Timeline (30 days)
            </Typography>
            <UserActivityChart userId={record?.id} />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Token Distribution
            </Typography>
            <TokenDistributionChart userId={record?.id} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const UserBetsSection = () => {
  const record = useRecordContext();
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    fetch(
      `/admin/api/bets?filter=${encodeURIComponent(JSON.stringify({ user_id: record.id }))}&range=[0,49]&sort=["created_at","DESC"]`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(r => r.ok ? r.json() : { data: [] })
      .then(json => setData(json.data || []))
      .catch(() => setData([]))
      .finally(() => setIsLoading(false));
  }, [record?.id]);

  if (isLoading) return <Loading />;

  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No bets found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Bet ID</TableCell>
            <TableCell>Event ID</TableCell>
            <TableCell>Token</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Win</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((bet: any) => (
            <TableRow key={bet.id}>
              <TableCell>{bet.id}</TableCell>
              <TableCell>{bet.event_id}</TableCell>
              <TableCell>{bet.token}</TableCell>
              <TableCell align="right">
                ${Number(bet.bet_amount_usd || 0).toFixed(2)}
              </TableCell>
              <TableCell>{bet.is_win ? '✅' : '❌'}</TableCell>
              <TableCell>
                {bet.created_at ? new Date(bet.created_at).toLocaleString() : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const UserPrizesSection = () => {
  const record = useRecordContext();
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    fetch(
      `/admin/api/prizes?filter=${encodeURIComponent(JSON.stringify({ user_id: record.id }))}&range=[0,49]&sort=["created_at","DESC"]`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(r => r.ok ? r.json() : { data: [] })
      .then(json => setData(json.data || []))
      .catch(() => setData([]))
      .finally(() => setIsLoading(false));
  }, [record?.id]);

  if (isLoading) return <Loading />;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Prize History
        </Typography>
        {!data || data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No prizes won yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prize ID</TableCell>
                  <TableCell>Event ID</TableCell>
                  <TableCell>Rank</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Won At</TableCell>
                  <TableCell>Paid At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((prize: any) => (
                  <TableRow key={prize.id}>
                    <TableCell>{prize.prize_id || prize.id}</TableCell>
                    <TableCell>{prize.event_id}</TableCell>
                    <TableCell>#{prize.rank}</TableCell>
                    <TableCell align="right">
                      ${Number(prize.amount_usd || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{prize.token}</TableCell>
                    <TableCell>
                      <Chip
                        label={prize.status}
                        color={
                          prize.status === 'SENT' ? 'success' :
                          prize.status === 'PENDING' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {prize.created_at ? new Date(prize.created_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {prize.paid_at ? new Date(prize.paid_at).toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Helper components
const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color?: string;
}) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: color || 'inherit' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num || 0);
};
